import type { ReactNode } from 'react';
import Warning from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import colors from 'styles/colors';
import type { StepToastProps } from './StepToast';
import { StepToastMessage } from './hpStepToastMessages';

/** Static display props for a StepToast (everything except onAction callback). */
export type ToastContent = Pick<StepToastProps, 'actionLabel' | 'icon'> & {
  message: StepToastMessage;
  /** Human-readable description of when this toast appears. */
  context: string;
};

// ── Icons ──────────────────────────────────────────────────────────────

// By default the toast icon is a green check. Define other icons here.
const DELETE_ICON: ReactNode = <Warning style={{ color: colors.yellow7 }} />;
const ERROR_ICON: ReactNode = <ErrorIcon style={{ color: colors.red4 }} />;

// ── Toast definitions ──────────────────────────────────────────────────

export const CLINICAL_STEP_SAVED_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalStepSaved,
  actionLabel: 'UNDO',
  context: 'After saving clinical step edits',
};

export const CLINICAL_STEP_REVERTED_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalStepReverted,
  actionLabel: 'UNDO',
  context: 'After reverting a clinical step to baseline',
};

export const CLINICAL_STEP_CREATED_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalStepCreated,
  context: 'After creating a new step in clinical mode',
};

export const CLINICAL_STEP_CREATED_WITH_REVERT_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalStepCreatedWithRevert,
  actionLabel: 'UNDO',
  context: 'After creating a new step when the current step had unsaved changes',
};

export const FORM_STEP_CREATED_TOAST: ToastContent = {
  message: StepToastMessage.FormStepCreated,
  actionLabel: 'UNDO',
  context: 'After adding a step in the HP form',
};

export const STEP_DELETED_TOAST: ToastContent = {
  message: StepToastMessage.StepDeleted,
  actionLabel: 'UNDO',
  icon: DELETE_ICON,
  context: 'After deleting a step',
};

export const CLINICAL_SAVE_FAILED_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalSaveFailed,
  icon: ERROR_ICON,
  context: 'When a clinical step save mutation fails',
};

export const CLINICAL_REORDER_FAILED_TOAST: ToastContent = {
  message: StepToastMessage.ClinicalReorderFailed,
  icon: ERROR_ICON,
  context: 'When a step reorder mutation fails',
};

export const CREATE_FAILED_TOAST: ToastContent = {
  message: StepToastMessage.CreateFailed,
  icon: ERROR_ICON,
  context: 'When a step create mutation fails',
};

export const DELETE_FAILED_TOAST: ToastContent = {
  message: StepToastMessage.DeleteFailed,
  icon: ERROR_ICON,
  context: 'When a step delete mutation fails',
};

export const RESET_ALL_TOAST: ToastContent = {
  message: StepToastMessage.AllStepsReverted,
  context: 'After reverting all steps to baseline',
};

/** All toast definitions, for iteration in stories. */
export const ALL_STEP_TOASTS: ToastContent[] = [
  CLINICAL_STEP_SAVED_TOAST,
  CLINICAL_STEP_REVERTED_TOAST,
  CLINICAL_STEP_CREATED_TOAST,
  CLINICAL_STEP_CREATED_WITH_REVERT_TOAST,
  FORM_STEP_CREATED_TOAST,
  STEP_DELETED_TOAST,
  CLINICAL_SAVE_FAILED_TOAST,
  CLINICAL_REORDER_FAILED_TOAST,
  CREATE_FAILED_TOAST,
  DELETE_FAILED_TOAST,
];
